import { X } from "lucide-react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDropdowns } from "../../inputs/UseDropdowns";
import { getAuthHeaders } from "@/utils/getauthheaders";

import ClientSection from "../../transportformcomponents/ClientSection";
import DepartureSection from "../../transportformcomponents/DepartureSection";
import DestinationSection from "../../transportformcomponents/DestinationSection";
import MultipleDestinationsSection from "../../transportformcomponents/MultipleDestinationSection";
import StatusPrioritySection from "../../transportformcomponents/StatusPrioritySection";
import FinancialSection from "../../transportformcomponents/FinancialSection";
import ResourcesSection from "../../transportformcomponents/ResourcesSection";
import DescriptionSection from "../../transportformcomponents/DescriptionSection";

// Tipuri mutate în afara componentei pentru evitarea re-creărilor
type MultipleDestination = {
  id?: string;
  destinatie: string;
  oras_destinatie: string;
  locatie_destinatie_id?: number;
  descriere?: string;
  telefon_contact?: string;
};

type TransportFormData = {
  client: string;
  client_id?: number;
  destinatie: string;
  oras_destinatie: string;
  locatie_plecare_id?: number;
  locatie_destinatie_id?: number;
  plecare_separata: boolean;
  adresa_plecare?: string;
  oras_plecare?: string;
  status: "in_asteptare" | "pe_drum" | "livrat" | "in_incarcare";
  prioritate: "normala" | "urgenta";
  descriere?: string;
  sofer_id?: number;
  vehicul_id?: number;
  value: number;
  tip_plata: "cash" | "card";
  ora_plecarii?: string;
  ora_estimata?: string;
  progres?: number;
  multiple_destinations: MultipleDestination[];
  create_separate_transports: boolean;
  telefon_contact: string; 
};

type TransportFormProps = {
  formData: TransportFormData;
  setFormData: React.Dispatch<React.SetStateAction<TransportFormData>>;
  onValidationChange?: (isValid: boolean) => void;
  isEditMode?: boolean;
  transportId?: string;
};

// Constante mutate în afara componentei
const API_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minute
const DEFAULT_DEPARTURE_OFFSET = 1; // ore
const DEFAULT_ARRIVAL_OFFSET = 4; // ore

// Funcții helper mutate în afara componentei pentru a evita re-creările
const formatDateTimeForInput = (dateValue: any): string => {
  if (!dateValue) return '';
  
  try {
    let date: Date;
    
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      if (!dateValue.trim()) return '';
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      console.warn('Invalid date format:', dateValue);
      return '';
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date created from:', dateValue);
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error, 'Original value:', dateValue);
    return '';
  }
};

const isValidDate = (dateValue: any): boolean => {
  if (!dateValue) return false;
  
  try {
    const date = new Date(dateValue);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};

// Cache pentru date client - folosim un singleton
const clientCache = {
  data: new Map<number, { data: any; timestamp: number }>(),
  
  get(id: number) {
    const cached = this.data.get(id);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > CACHE_EXPIRY) {
      this.data.delete(id);
      return null;
    }
    
    return cached.data;
  },
  
  set(id: number, data: any) {
    this.data.set(id, {
      data,
      timestamp: Date.now()
    });
  }
};

const TransportForm: React.FC<TransportFormProps> = ({ 
  formData, 
  setFormData,
  onValidationChange,
  isEditMode = false,
  transportId
}) => {
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [driverVehicle, setDriverVehicle] = useState<any>(null);
  const [vehicleError, setVehicleError] = useState<string>("");
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  
  // Refs pentru controlul efectelor - consolidate într-un singur obiect
  const stateRefs = useRef({
    hasSetDefaultTime: false,
    hasSetEstimatedTime: false,
    previousClient: null as number | null,
    previousPlecareSeparata: false,
    isInitialized: false
  });

  const { data: dropdownsData, loading: dropdownsLoading, error: dropdownsError, refetch } = useDropdowns();

  // User role check - memoizat pentru a evita re-parsarea
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || '{}'), []);
  const isDispacer = useMemo(() => user.role === "dispacer", [user.role]);

  // Memoizare pentru validare
  const isFormValid = useMemo(() => {
    return !formData.sofer_id || (formData.sofer_id && formData.vehicul_id);
  }, [formData.sofer_id, formData.vehicul_id]);

  // Effect pentru validare
  useEffect(() => {
    onValidationChange?.(isFormValid);
  }, [isFormValid, onValidationChange]);

  // Funcții helper memoizate doar acolo unde depind de props/state
  const getClientLocationId = useCallback((clientId: number) => {
    return dropdownsData.clients.find(c => c.id === clientId)?.location_id;
  }, [dropdownsData.clients]);

  // Load transport for edit - optimizat cu mai puține dependențe
  const loadTransportForEdit = useCallback(async (id: string) => {
    if (stateRefs.current.isInitialized) return;
    
    try {
      setIsLoadingEdit(true);
      const response = await fetch(`${API_BASE_URL}/shipments/${id}/details`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const transport = data.data;
        
        if (transport.raw_data) {
          const rawData = transport.raw_data;
          
          const formattedOraPlecarii = formatDateTimeForInput(rawData.ora_plecarii);
          const formattedOraEstimata = formatDateTimeForInput(rawData.ora_estimata);
          
          const clientLocationId = getClientLocationId(rawData.client_id);
          const isPlecareSeparata = Boolean(rawData.locatie_plecare_id && 
            rawData.locatie_plecare_id !== clientLocationId);

          // Actualizare în batch pentru a evita re-renderurile multiple
          setFormData(prev => ({
          ...prev,
          client: rawData.client || '',
          client_id: rawData.client_id,
          destinatie: transport.locations?.destinatie?.adresa || rawData.destinatie || '',
          oras_destinatie: transport.locations?.destinatie?.nume || '',
          oras_plecare: transport.locations?.plecare?.nume || '',
          locatie_plecare_id: rawData.locatie_plecare_id,
          locatie_destinatie_id: rawData.locatie_destinatie_id,
          plecare_separata: isPlecareSeparata,
          adresa_plecare: transport.locations?.plecare?.adresa || '',
          status: rawData.status,
          prioritate: rawData.prioritate,
          descriere: rawData.descriere || '',
          value: Number(rawData.value) || 0,
          tip_plata: rawData.tip_plata || 'cash',
          sofer_id: rawData.sofer_id,
          vehicul_id: rawData.vehicul_id,
          ora_plecarii: formattedOraPlecarii,
          ora_estimata: formattedOraEstimata,
          progres: Number(rawData.progres) || 0,
          telefon_contact: rawData.telefon_contact || '',
          // Adaugă câmpurile lipsă pentru destinatar
          telefon_destinatar: rawData.telefon_destinatar || '',
          nume_destinatar: rawData.nume_destinatar || '',
          // Inițializează câmpurile pentru multiple destinații
          multiple_destinations: [],
          create_separate_transports: false
        }));

          // Actualizarea stării în batch
          const client = dropdownsData.clients.find(c => c.id === rawData.client_id);
          const driver = dropdownsData.drivers.find(d => d.id === rawData.sofer_id);
          const vehicle = dropdownsData.vehicles.find(v => v.id === rawData.vehicul_id);

          if (client) {
            setSelectedClient(client);
            stateRefs.current.previousClient = client.id;
          }

          if (driver) {
            setSelectedDriver(driver);
            if (vehicle) {
              setDriverVehicle(vehicle);
            }
          }

          // Actualizarea refs în batch
          Object.assign(stateRefs.current, {
            hasSetDefaultTime: true,
            hasSetEstimatedTime: true,
            previousPlecareSeparata: isPlecareSeparata,
            isInitialized: true
          });
        }
      }
    } catch (error) {
      console.error('Eroare la încărcarea transportului:', error);
    } finally {
      setIsLoadingEdit(false);
    }
  }, [dropdownsData.clients, dropdownsData.drivers, dropdownsData.vehicles, getClientLocationId, setFormData]);

  // Effects consolidate pentru inițializare
  useEffect(() => {
    if (isEditMode && transportId && dropdownsData.clients.length > 0 && !stateRefs.current.isInitialized) {
      loadTransportForEdit(transportId);
      return;
    }

    // Setarea timpului default - consolidată în același effect
    if (!formData.ora_plecarii && !isEditMode && !isLoadingEdit && !stateRefs.current.hasSetDefaultTime) {
      stateRefs.current.hasSetDefaultTime = true;
      const now = new Date();
      now.setHours(now.getHours() + DEFAULT_DEPARTURE_OFFSET);
      const defaultDateTime = formatDateTimeForInput(now);
      setFormData(prev => ({ ...prev, ora_plecarii: defaultDateTime }));
    }

    // Setarea timpului estimat - consolidată în același effect
    if (formData.ora_plecarii && 
        !formData.ora_estimata && 
        !isEditMode && 
        !isLoadingEdit && 
        !stateRefs.current.hasSetEstimatedTime) {
      try {
        const departure = new Date(formData.ora_plecarii);
        if (isValidDate(departure)) {
          stateRefs.current.hasSetEstimatedTime = true;
          departure.setHours(departure.getHours() + DEFAULT_ARRIVAL_OFFSET);
          const estimatedDateTime = formatDateTimeForInput(departure);
          setFormData(prev => ({ ...prev, ora_estimata: estimatedDateTime }));
        }
      } catch (error) {
        console.error('Error setting estimated time:', error);
      }
    }
  }, [
    isEditMode, 
    transportId, 
    dropdownsData.clients.length, 
    formData.ora_plecarii, 
    formData.ora_estimata,
    isLoadingEdit,
    loadTransportForEdit, 
    setFormData
  ]);

  // Handler pentru client - optimizat cu cache îmbunătățit
  const handleClientChange = useCallback(async (clientId: number | null, client?: any) => {
    if (clientId && client) {
      let clientLocation = null;
      
      // Încercăm mai întâi în dropdowns
      const clientFromDropdowns = dropdownsData.clients.find(c => c.id === clientId);
      
      if (clientFromDropdowns?.location_id) {
        clientLocation = {
          id: clientFromDropdowns.location_id,
          adresa: clientFromDropdowns.address,
          oras: clientFromDropdowns.city
        };
        setSelectedClient(clientFromDropdowns);
      } else {
        // Încercăm în cache
        const cached = clientCache.get(clientId);
        
        if (cached) {
          if (cached.location_id) {
            clientLocation = {
              id: cached.location_id,
              adresa: cached.address,
              oras: cached.city
            };
          }
          setSelectedClient(cached);
        } else {
          // Fetch doar dacă nu avem în cache
          try {
            const response = await fetch(`${API_BASE_URL}/clients/${clientId}`, {
              headers: getAuthHeaders(),
            });
            if (response.ok) {
              const responseData = await response.json();
              const clientDetails = responseData.success ? responseData.client : responseData;
              
              clientCache.set(clientId, clientDetails);
              
              if (clientDetails?.location_id) {
                clientLocation = {
                  id: clientDetails.location_id,
                  adresa: clientDetails.address,
                  oras: clientDetails.city
                };
              }
              setSelectedClient(clientDetails);
            }
          } catch (err) {
            console.error("Eroare fetch client:", err);
            setSelectedClient(null);
          }
        }
      }

      setFormData(prev => {
        const newData = {
          ...prev,
          client: client.denumire || client.nume_firma,
          client_id: clientId,
        };

        if (!prev.plecare_separata && clientLocation) {
          Object.assign(newData, {
            locatie_plecare_id: clientLocation.id,
            adresa_plecare: clientLocation.adresa,
            oras_plecare: clientLocation.oras
          });
        }

        return newData;
      });

      stateRefs.current.previousClient = clientId;
    } else {
      setFormData(prev => ({ 
        ...prev, 
        client: "",
        client_id: undefined,
        plecare_separata: false,
        locatie_plecare_id: undefined,
        adresa_plecare: undefined,
        oras_plecare: undefined
      }));
      setSelectedClient(null);
      stateRefs.current.previousClient = null;
    }
  }, [dropdownsData.clients, setFormData]);

  // Restul handler-urilor rămân la fel, dar cu mici optimizări
  const handlePlecareSerparata = useCallback((checked: boolean) => {
    if (checked) {
      setFormData(prev => ({ 
        ...prev, 
        plecare_separata: true,
        locatie_plecare_id: undefined,
        adresa_plecare: undefined,
        oras_plecare: undefined
      }));
    } else if (selectedClient?.location_id) {
      setFormData(prev => ({ 
        ...prev, 
        plecare_separata: false,
        locatie_plecare_id: selectedClient.location_id,
        adresa_plecare: selectedClient.address,
        oras_plecare: selectedClient.city
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        plecare_separata: false,
        locatie_plecare_id: undefined,
        adresa_plecare: undefined,
        oras_plecare: undefined
      }));
    }
  }, [selectedClient, setFormData]);

  const handleDriverChange = useCallback((driverId: number | null, driverName?: string, driver?: any) => {
    setVehicleError("");
    
    if (driverId && driver) {
      setSelectedDriver(driver);
      
      const assignedVehicle = dropdownsData.vehicles.find(v => 
        v.assigned_employee_id === driverId
      );
      
      if (assignedVehicle) {
        setDriverVehicle(assignedVehicle);
        setFormData(prev => ({ 
          ...prev, 
          sofer_id: driverId,
          vehicul_id: assignedVehicle.id
        }));
      } else {
        setDriverVehicle(null);
        setVehicleError("Șoferul selectat nu are vehicul asignat. Nu se poate crea transport.");
        setFormData(prev => ({ 
          ...prev, 
          sofer_id: driverId,
          vehicul_id: undefined
        }));
      }
    } else {
      setSelectedDriver(null);
      setDriverVehicle(null);
      setVehicleError("");
      setFormData(prev => ({ 
        ...prev, 
        sofer_id: undefined,
        vehicul_id: undefined
      }));
    }
  }, [dropdownsData.vehicles, setFormData]);

  const handleVehicleChange = useCallback((vehicleId: number | null, vehicle?: any) => {
    if (!formData.sofer_id) {
      setFormData(prev => ({ 
        ...prev, 
        vehicul_id: vehicleId || undefined 
      }));
    }
  }, [formData.sofer_id, setFormData]);

  const handleDepartureLocationChange = useCallback((location: any) => {
    setFormData(prev => location ? ({ 
      ...prev, 
      locatie_plecare_id: location.id,
      adresa_plecare: location.adresa,
      oras_plecare: location.oras || prev.oras_plecare
    }) : ({
      ...prev,
      locatie_plecare_id: undefined
    }));
  }, [setFormData]);

  const handleDestinationLocationChange = useCallback((location: any) => {
    setFormData(prev => location ? ({ 
      ...prev, 
      locatie_destinatie_id: location.id,
      destinatie: location.adresa,
      oras_destinatie: location.oras || prev.oras_destinatie
    }) : ({
      ...prev,
      locatie_destinatie_id: undefined
    }));
  }, [setFormData]);

  const handleCityChange = useCallback((city: { name: string; county: string; countyCode: string }) => {
    setFormData(prev => ({ 
      ...prev, 
      oras_destinatie: city?.name || '',
      locatie_destinatie_id: undefined 
    }));
  }, [setFormData]);

  const handleDepartureCityChange = useCallback((city: { name: string; county: string; countyCode: string }) => {
    setFormData(prev => ({ 
      ...prev, 
      oras_plecare: city?.name || '',
      locatie_plecare_id: undefined 
    }));
  }, [setFormData]);

  // Early returns pentru loading/error states
  if (dropdownsLoading || (isEditMode && isLoadingEdit)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isLoadingEdit ? "Se încarcă datele transportului..." : "Se încarcă datele..."}
          </p>
        </div>
      </div>
    );
  }

  if (dropdownsError) {
    return (
      <div className="p-8 text-center">
        <X className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Eroare la încărcare</h3>
        <p className="text-gray-600 mb-4">{dropdownsError}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Încearcă din nou
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ClientSection
        formData={formData}
        setFormData={setFormData}
        selectedClient={selectedClient}
        handleClientChange={handleClientChange}
        handlePlecareSerparata={handlePlecareSerparata}
        dropdownsData={dropdownsData}
      />

      <DepartureSection
        formData={formData}
        setFormData={setFormData}
        handleDepartureLocationChange={handleDepartureLocationChange}
        handleDepartureCityChange={handleDepartureCityChange}
      />

      <DestinationSection
        formData={formData}
        setFormData={setFormData}
        handleDestinationLocationChange={handleDestinationLocationChange}
        handleCityChange={handleCityChange}
      />
{!isEditMode &&(
      <MultipleDestinationsSection
        formData={formData}
        setFormData={setFormData}
      />
)}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatusPrioritySection
          formData={formData}
          setFormData={setFormData}
        />

        <FinancialSection
          formData={formData}
          setFormData={setFormData}
          isDispacer={isDispacer}
          isEditMode={isEditMode}
        />
      </div>

      <ResourcesSection
        formData={formData}
        setFormData={setFormData}
        handleDriverChange={handleDriverChange}
        handleVehicleChange={handleVehicleChange}
        vehicleError={vehicleError}
        driverVehicle={driverVehicle}
        dropdownsData={dropdownsData}
      />

      <DescriptionSection
        formData={formData}
        setFormData={setFormData}
      />
    </div>
  );
};

export default TransportForm;